import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { CssBaseline } from "@mui/material";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useIntercom } from "react-use-intercom";
import type { PropsWithChildren } from "react";
import { ErrorBoundary } from "@sentry/react";
import { useAuth, PaymentMethod } from "@/features/auth";
import { useClarityTracking } from "@/hooks";
import { NotFound } from "@/features/not-found";

export interface RootRouteContext {
  auth: ReturnType<typeof useAuth>;
}

const IntercomInitializer = ({ children }: PropsWithChildren) => {
  const { user } = useAuth();
  const { boot, update } = useIntercom();
  const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;

  if (user) {
    const isFreePlanUser = user.payment_method === PaymentMethod.Free;

    const payload = {
      app_id: INTERCOM_APP_ID,
      email: user.email,
    };

    boot(payload);
    update({ hideDefaultLauncher: isFreePlanUser });
  }

  return children;
};

const ClarityTracker = () => {
  useClarityTracking();
  return null;
};

const RootComponent = () => {
  return (
    <>
      <CssBaseline />
      <ErrorBoundary>
        <IntercomInitializer>
          <ClarityTracker />
          <Outlet />
        </IntercomInitializer>
      </ErrorBoundary>
      <TanStackRouterDevtools />
    </>
  );
};

const Route = createRootRouteWithContext<RootRouteContext>()({
  component: RootComponent,
  notFoundComponent: () => <NotFound />,
});

export { Route };
